import { useState, useEffect } from "react";
import Login from "./scenes/auth/login";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import { useOrion } from "./contexts/OrionContext";
import Dashboard from "./scenes/dashboard";
import { CssBaseline, ThemeProvider, Box, Typography } from "@mui/material";
import theme from "./theme";
import CheckLogin from "./scenes/auth/checkLogin";
import ContentDetail from "./scenes/security/contents";
import Critical from "./scenes/security/critical";
import Movements from "./scenes/security/movements";
import Alert from "./scenes/security/alert";
import InternalDetection from "./scenes/security/internal-detection";
import ThreatIntelligence from "./scenes/security/threat-intelligence";
import TiresiaEntropy from "./scenes/tiresia-engine/network-entropy";
import Status from "./scenes/network-status/status";
import AssetDetailPage from "./scenes/network-status/asset-detail";
import AssetsOverviewPage from "./scenes/network-status/overview";
import NetworkZonesPage from "./scenes/network-status/zones";
import InfrastructureLog from "./scenes/network-status/infrastructure-log";
import Map from "./scenes/extra/map";
import Ticket from "./scenes/support/ticket";
import ChooseLogin from "./chooseLogin";
import DashboardR from "./scenes-reseller/dashboard";
import Verification from "./scenes/auth/verify";
import SidebarR from "./scenes-reseller/global/Sidebar";
import TopbarR from "./scenes-reseller/global/Topbar";
import Customers from "./scenes-reseller/customersOption/customers";
import NFR from "./scenes-reseller/customersOption/nfr";
import TicketR from "./scenes-reseller/tickets";
import ResellerAccount from "./scenes-reseller/options";
import Registration from "./scenes/auth/registration";
import ForgotPassword from "./scenes/auth/forgotPassword";
import ResetPassword from "./scenes/auth/resetPassword";
import SidebarMobile from "./scenes/global/Sidebar-mobile";
import SidebarMobileR from "./scenes-reseller/global/Sidebar-mobile";
import NotifyTable from "./components/notifyTable";
import AdvancedV from "./scenes/tiresia-engine/advancedVisualizer";
import High from "./scenes/security/high";
import API from "./api";
import Icos from "./scenes-reseller/icos";
import NotFound from "./components/notFound";
import ChangeLog from "./components/changeLog";
import Report from "./scenes-reseller/report";
import IpInspection from "./scenes/extra/ipInspection";
import AllLecsView from "./scenes/allLecsView";
import Highlights from "./scenes/security/highlights";
import UserOptions from "./scenes/options";
import LecsOptions from "./scenes-reseller/customersOption/lecsOptions";
import { LicenseInfo } from '@mui/x-license';
import Tiresia from "./scenes/tiresia-engine/tiresia";
import IncidentContext from "./scenes/tiresia-engine/incident-context";
import LecsManagement from "./scenes/lecsManagement";
import AccountManagement from "./scenes-reseller/customersOption/accounts";
import DealProtection from "./scenes-reseller/deal-protection";
import Downloads from "./scenes-reseller/downloads";
import LicensesManagement from "./scenes-reseller/license";
import { ToastContainer } from "react-toastify";
import TiresiaNetworkPage from "./scenes/network-status/tiresia-network";
import './style/notificationColor.css';
import SidebarC from "./scenes-reseller/global/SidebarC";
import DashboardC from "./scenes-reseller/dashboard/DashboardC";
import AutomaticReport from "./scenes-reseller/AutomaticReport";
import Agent from "./scenes/agent/monitor";
import SystemDetection from "./scenes/agent/systemDetection";
import AgentAnalyses from "./scenes/agent/analyses";
import Logs from './scenes/logs';
import ChooseDashboard from "./scenes/auth/chooseDashboard";
import Aggregations from "./scenes/aggregations";
import OtProtocols from "./scenes/security/ot-protocols";
import OtWrites from "./scenes/security/ot-writes";
import OtReads from "./scenes/security/ot-reads";
import OtMonitor from "./scenes/security/ot-monitor";
import OtConfiguration from "./scenes/security/ot-configuration";
import OtSecurity from "./scenes/security/ot-security";
import ApiDocs from "./scenes-reseller/api-docs";
import { toastError } from "./utils/notification-loading";
import DownloadPackagePage from "./scenes/agent/download";
import TableLicensesAgent from "./scenes/agent/licenses";
import { SettingsProvider } from "./contexts/SettingsContext";

// ─── Layout Components (defined OUTSIDE App to keep stable references) ────────

function MainLayout({ isCollapsed, handleChangeSidebar, otEnabled, orionEnabled }) {
  const [isSidebar, setIsSidebar] = useState(true);

  return (
    <CheckLogin>
      <SettingsProvider>
      <div className="app">
        {window.innerWidth > 1024
          ? [
              <Sidebar
                key="sidebar"
                isCollapsed={isCollapsed}
                handleChange={handleChangeSidebar}
                otEnabled={otEnabled}
                orionEnabled={orionEnabled}
              />,
              <main
                key="main"
                className="relative h-full w-full flex-1 overflow-auto transition-auto"
              >
                <Topbar
                  className="sticky top-0 flex justify-between z-10 h-14 p-2"
                  setIsSidebar={setIsSidebar}
                />
                <Outlet />
              </main>,
            ]
          : [
              <SidebarMobile
                key="sidebarMobile"
                isSidebar={isSidebar}
                className="sticky top-0 flex justify-between z-10 h-14 p-2"
              />,
              <main
                key="main-mobile"
                className="relative h-full w-full flex-1 overflow-auto transition-auto"
                style={{ paddingBottom: "65px" }}
              >
                <Topbar
                  className="sticky top-0 flex justify-between z-10 h-14 p-2"
                  setIsSidebar={setIsSidebar}
                />
                <Outlet />
              </main>,
            ]}
      </div>
      </SettingsProvider>
    </CheckLogin>
  );
}

function MainLayoutR({ customerName, commercial, isCollapsed, handleChangeSidebar }) {
  const [isSidebar, setIsSidebar] = useState(true);

  return (
    <CheckLogin>
      <SettingsProvider>
      <div className="app">
        {window.innerWidth > 1024 ? (
          <>
            {commercial === "true" ? (
              <SidebarC
                key="sidebar-r"
                isCollapsed={isCollapsed}
                handleChange={handleChangeSidebar}
              />
            ) : (
              <SidebarR
                key="sidebar-r"
                customerName={customerName}
                isCollapsed={isCollapsed}
                handleChange={handleChangeSidebar}
              />
            )}
            <main
              key="main-r"
              id="main"
              className="relative h-full w-full flex-1 overflow-auto transition-auto"
            >
              <TopbarR
                customerName={customerName}
                className="sticky top-0 flex justify-between z-10 h-14 p-2"
                setIsSidebar={setIsSidebar}
              />
              <Outlet />
            </main>
          </>
        ) : (
          <>
            <SidebarMobileR
              key="sidebar-mobile-r"
              customerName={customerName}
              isSidebar={isSidebar}
              className="sticky top-0 flex justify-between z-10 h-14 p-2"
            />
            <main
              key="main-mobile-r"
              className="relative h-full w-full flex-1 overflow-auto transition-auto"
            >
              <TopbarR
                className="sticky top-0 flex justify-between z-10 h-14 p-2"
                setIsSidebar={setIsSidebar}
              />
              <Outlet />
            </main>
          </>
        )}
      </div>
      </SettingsProvider>
    </CheckLogin>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

const OtModuleGuard = ({ enabled, children, title = "OT Module not available", subtitle = "This feature requires an active OT license." }) => {
  if (enabled) return children;
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" gap={2}>
      <Typography variant="h5" color="text.secondary" fontWeight={600}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
    </Box>
  );
};

function App() {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE);
  const [tipo, setTipo] = useState(window.sessionStorage.getItem("type"));
  const [customerName, setCustomerName] = useState("");
  const [lecsInfo, setLecsInfo] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(JSON.parse(sessionStorage.getItem('sidebar')) ?? false);
  const [commercial] = useState(sessionStorage.getItem("commercial") || false);
  const [otEnabled, setOtEnabled] = useState(true);
  const { orionEnabled } = useOrion();

  const handleChangeSidebar = () => {
    API.setSidebar({ value: !isCollapsed }).then(() => {
      sessionStorage.setItem('sidebar', !isCollapsed);
      setIsCollapsed(!isCollapsed);
    }).catch(() => {
      toastError("Sidebar not changed");
    });
  };

  useEffect(() => {
    API.getCustomerFeatures().then((res) => {
      setOtEnabled(res.data.otModule);
    }).catch((err) => {
      console.error('[App] Failed to fetch license:', err);
      setOtEnabled(false);
    });
  }, []);

  useEffect(() => {
    if (customerName === "") {
      setCustomerName(window.sessionStorage.getItem('customerName') ? window.sessionStorage.getItem('customerName') : "");
    } else {
      window.sessionStorage.setItem('customerName', customerName);
    }
  }, [customerName]);

  const location = useLocation();

  useEffect(() => {
    const tabMap = {
      "/user/home": "Dashboard",
      "/user/tiresia-engine/highlights": "Highlights",
      "/user/aggregations": "Aggregations",
      "/user/security/critical": "Critical",
      "/user/security/high": "High",
      "/user/security/alert": "Alert",
      "/user/security/movements": "Movements",
      "/user/security/threat-intelligence": "Threat Intelligence",
      "/user/security/ot-writes": "OT Writes",
      "/user/security/ot-protocols": "Industrial Protocols",
      "/user/security/ot-reads": "OT Reads",
      "/user/security/ot-security": "OT Security",
      "/user/security/ot-monitor": "OT Monitor",
      "/user/security/ot-configuration": "OT Tag Configuration",
      "/user/extra/map": "Map",
      "/user/extra/ip-inspection": "IP Inspection",
      "/user/tiresia-engine/tiresia-analysis": "Tires-IA Analysis",
      "/user/tiresia-engine/incident-context": "Incident Context",
      "/user/tiresia-engine/network-entropy": "Network Entropy",
      "/user/tiresia-engine/advanced-visualizer": "Advanced Visualizer",
      "/user/network-status/overview": "Overview",
      "/user/network-status/zones": "Network Zones",
      "/user/network-status/status": "Inventory",
      "/user/network-status/asset-detail": "Asset Detail",
      "/user/network-status/infrastructure-log": "Infrastructure Log",
      "/user/support/ticket": "Ticket",
      "/user/all-lecs-view": "Multi LECS View",
      "/user/changelog": "Change Log",
    };

    const selectedTab = Object.keys(tabMap).find(path =>
      location.pathname.includes(path)
    );

    if (selectedTab) {
      sessionStorage.setItem("selectedTab", tabMap[selectedTab]);
    }

    if (!location.pathname.includes("/reseller/myC")) {
      setCustomerName("");
      sessionStorage.removeItem("customerName");
    }
    setTipo(sessionStorage.getItem("type"));
  }, [location.pathname]);

  window.addEventListener("orientationchange", () => {
    window.location.reload();
  });

  if (!tipo || tipo === "null") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/" element={<ChooseLogin setTipo={setTipo} tipo={tipo} />} />
          <Route path="/auth/passwordReset" element={<ResetPassword />} />
        </Routes>
      </ThemeProvider>
    );
  }

  if (tipo === "user") {
    return (
      <>
        <ToastContainer position="top-center" autoClose={5000} theme="dark" />
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route path="/" element={<ChooseLogin setTipo={setTipo} tipo={tipo} />} />
            <Route path="/auth/passwordReset" element={<ResetPassword />} />
            <Route path="/user/login" element={<Login tipo={tipo} />} />
            <Route path="/user/twofactorauthentication" element={<Verification tipo={tipo} />} />
            <Route path="/user/choose" element={<ChooseDashboard />} />
            <Route path="/user/registration" element={<Registration tipo={tipo} />} />
            <Route path="/user/forgotPassword" element={<ForgotPassword tipo={tipo} />} />

            <Route
              path="/user/"
              element={
                <MainLayout
                  isCollapsed={isCollapsed}
                  handleChangeSidebar={handleChangeSidebar}
                  otEnabled={otEnabled}
                  orionEnabled={orionEnabled}
                />
              }
            >
              <Route path="/user/home/" element={<Dashboard />} />
              <Route path="/user/aggregations/" element={<Aggregations />} />
              <Route path="/user/security/critical" element={<Critical />} />
              <Route path="/user/security/high" element={<High />} />
              <Route path="/user/security/movements" element={<Movements />} />
              <Route path="/user/security/alert" element={<Alert />} />
              <Route path="/user/security/ot-writes" element={<OtModuleGuard enabled={otEnabled}><OtWrites /></OtModuleGuard>} />
              <Route path="/user/security/ot-protocols" element={<OtModuleGuard enabled={otEnabled}><OtProtocols /></OtModuleGuard>} />
              <Route path="/user/security/ot-reads" element={<OtModuleGuard enabled={otEnabled}><OtReads /></OtModuleGuard>} />
              <Route path="/user/security/ot-security" element={<OtModuleGuard enabled={otEnabled}><OtSecurity /></OtModuleGuard>} />
              <Route path="/user/security/ot-monitor" element={<OtModuleGuard enabled={otEnabled}><OtMonitor /></OtModuleGuard>} />
              <Route path="/user/security/ot-configuration" element={<OtModuleGuard enabled={otEnabled}><OtConfiguration /></OtModuleGuard>} />
              <Route path="/user/security/contents" element={<ContentDetail />} />
              <Route path="/user/security/internal-detection" element={<InternalDetection />} />
              <Route path="/user/security/threat-intelligence" element={<ThreatIntelligence />} />
              <Route path="/user/tiresia-engine/highlights" element={<Highlights />} />
              <Route path="/user/tiresia-engine/network-entropy" element={<TiresiaEntropy />} />
              <Route path="/user/network-status/overview" element={<AssetsOverviewPage />} />
              <Route path="/user/network-status/zones" element={<OtModuleGuard enabled={orionEnabled} title="Network Zones not available" subtitle="This feature requires Orion visibility to be enabled."><NetworkZonesPage /></OtModuleGuard>} />
              <Route path="/user/network-status/status" element={<Status />} />
              <Route path="/user/network-status/asset-detail" element={<AssetDetailPage />} />
              <Route path="/user/network-status/infrastructure-log" element={<InfrastructureLog />} />
              <Route path="/user/network-status/tiresia-network" element={<TiresiaNetworkPage />} />
              <Route path="/user/agent/monitor" element={<Agent />} />
              <Route path="/user/agent/system-detection" element={<SystemDetection />} />
              <Route path="/user/agent/analyses" element={<AgentAnalyses />} />
              <Route path="/user/agent/download" element={<DownloadPackagePage />} />
              <Route path="/user/agent/licenses" element={<TableLicensesAgent />} />
              <Route path="/user/extra/map" element={<Map />} />
              <Route path="/user/options" element={<UserOptions />} />
              <Route path="/user/extra/ip-inspection" element={<IpInspection />} />
              <Route path="/user/support/ticket" element={<Ticket />} />
              <Route path="/user/tiresia-engine/tiresia-analysis" element={<Tiresia />} />
              <Route path="/user/tiresia-engine/incident-context" element={<IncidentContext otEnabled={otEnabled} />} />
              <Route path="/user/tiresia-engine/advanced-visualizer" element={<AdvancedV />} />
              <Route path="/user/notifications" element={<NotifyTable />} />
              <Route path="/user/changelog" element={<ChangeLog />} />
              <Route path="/user/all-lecs-view" element={<AllLecsView />} />
              <Route path="/user/lecs-management" element={<LecsManagement />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ThemeProvider>
      </>
    );
  }

  if (tipo === "reseller") {
    return (
      <>
        <ToastContainer position="top-center" autoClose={5000} theme="colored" />
        <ReactNotifications />
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route path="/" element={<ChooseLogin setTipo={setTipo} tipo={tipo} />} />
            <Route path="/auth/passwordReset" element={<ResetPassword />} />
            <Route path="/reseller/login" element={<Login tipo={tipo} />} />
            <Route path="/reseller/twofactorauthentication" element={<Verification tipo={tipo} />} />
            <Route path="/reseller/registration" element={<Registration tipo={tipo} />} />
            <Route path="/reseller/forgotPassword" element={<ForgotPassword tipo={tipo} />} />

            <Route
              path="/reseller/"
              element={
                <MainLayoutR
                  customerName={customerName}
                  lecsInfo={lecsInfo}
                  commercial={commercial}
                  isCollapsed={isCollapsed}
                  handleChangeSidebar={handleChangeSidebar}
                />
              }
            >
              <Route
                path="/reseller/home/"
                element={
                  commercial === "true"
                    ? <DashboardC setCustomerName={setCustomerName} setLecsInfo={setLecsInfo} />
                    : <DashboardR setCustomerName={setCustomerName} setLecsInfo={setLecsInfo} />
                }
              />
              <Route path="/reseller/myC/customers" element={<Customers setCustomerName={setCustomerName} setLecsInfo={setLecsInfo} />} />
              <Route path="/reseller/myC/dashboard" element={<Dashboard />} />
              <Route path="/reseller/myC/lecs-options" element={<LecsOptions customerName={customerName} lecsInfo={lecsInfo} />} />
              <Route path="/reseller/myC/highlights" element={<Highlights customerName={customerName} />} />
              <Route path="/reseller/myC/aggregations" element={<Aggregations />} />
              <Route path="/reseller/myC/critical" element={<Critical customerName={customerName} />} />
              <Route path="/reseller/myC/high" element={<High customerName={customerName} />} />
              <Route path="/reseller/myC/movements" element={<Movements customerName={customerName} />} />
              <Route path="/reseller/myC/alert" element={<Alert customerName={customerName} />} />
              <Route path="/reseller/myC/ot-writes" element={<OtModuleGuard enabled={otEnabled}><OtWrites /></OtModuleGuard>} />
              <Route path="/reseller/myC/ot-protocols" element={<OtModuleGuard enabled={otEnabled}><OtProtocols /></OtModuleGuard>} />
              <Route path="/reseller/myC/ot-reads" element={<OtModuleGuard enabled={otEnabled}><OtReads /></OtModuleGuard>} />
              <Route path="/reseller/myC/ot-security" element={<OtModuleGuard enabled={otEnabled}><OtSecurity /></OtModuleGuard>} />
              <Route path="/reseller/myC/ot-monitor" element={<OtModuleGuard enabled={otEnabled}><OtMonitor /></OtModuleGuard>} />
              <Route path="/reseller/myC/ot-configuration" element={<OtModuleGuard enabled={otEnabled}><OtConfiguration /></OtModuleGuard>} />
              <Route path="/reseller/myC/internal-detection" element={<InternalDetection customerName={customerName} />} />
              <Route path="/reseller/myC/extra/map" element={<Map customerName={customerName} />} />
              <Route path="/reseller/myC/extra/ip-inspection" element={<IpInspection />} />
              <Route path="/reseller/myC/network-entropy" element={<TiresiaEntropy customerName={customerName} />} />
              <Route path="/reseller/myC/overview" element={<AssetsOverviewPage customerName={customerName} />} />
              <Route path="/reseller/myC/network-status/zones" element={<OtModuleGuard enabled={orionEnabled} title="Network Zones not available" subtitle="This feature requires Orion visibility to be enabled."><NetworkZonesPage customerName={customerName} /></OtModuleGuard>} />
              <Route path="/reseller/myC/status" element={<Status customerName={customerName} />} />
              <Route path="/reseller/myC/asset-detail" element={<AssetDetailPage />} />
              <Route path="/reseller/myC/infrastructure-log" element={<InfrastructureLog customerName={customerName} />} />
              <Route path="/reseller/myC/threat-intelligence" element={<ThreatIntelligence customerName={customerName} />} />
              <Route path="/reseller/myC/nfr" element={<NFR setCustomerName={setCustomerName} />} />
              <Route path="/reseller/account_management" element={<AccountManagement />} />
              <Route path="/reseller/licenses" element={<LicensesManagement />} />
              <Route path="/reseller/myC/agent/licenses" element={<TableLicensesAgent />} />
              <Route path="/reseller/agent/monitor" element={<Agent />} />
              <Route path="/reseller/logs" element={<Logs />} />
              <Route path="/reseller/options" element={<ResellerAccount />} />
              <Route path="/reseller/myC/tiresia-analysis" element={<Tiresia />} />
              <Route path="/reseller/myC/incident-context" element={<IncidentContext otEnabled={otEnabled} />} />
              <Route path="/reseller/myC/advanced-visualizer" element={<AdvancedV customerName={customerName} />} />
              <Route path="/reseller/myC/lecs-management" element={<LecsManagement />} />
              <Route path="/reseller/deal-protection" element={<DealProtection />} />
              <Route path="/reseller/api-documentation" element={<ApiDocs />} />
              <Route path="/reseller/document" element={<Downloads />} />
              <Route path="/reseller/tickets" element={<TicketR />} />
              <Route path="/reseller/material" element={<ResellerAccount />} />
              <Route path="/reseller/produce" element={<Icos />} />
              <Route path="/reseller/myC/report" element={<Report tipo={tipo} customerName={customerName} />} />
              <Route path="/reseller/myC/AutomaticReport" element={<AutomaticReport tipo={tipo} customerName={customerName} />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ThemeProvider>
      </>
    );
  }
}

export default App;